// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE from './config';
import Navbar from './components/Navbar';
import Loading from './components/Loading';
import Login from './components/Login';
import translations from './translations';
import { LanguageProvider } from './LanguageContext';
import WorkspacePicker from './components/WorkspacePicker';
import AllInventory from './components/AllInventory';
import RawMaterials from './components/RawMaterials';
import RawMaterialDetail from './components/RawMaterialDetail';
import Management from './components/Management';
import Products from './components/Products';
import ProductDetail from './components/ProductDetail';
import Account from './components/Account';
import Dispatch from './components/Dispatch';
import Analysis from './components/Analysis';
import Health from './components/Health';
import PackingMaterials from './components/PackingMaterials';
import Activity from './components/Activity';
import AddWorkspaces from './components/AddWorkspaces';

const WorkspaceLayout = ({ user, handleLogout, currentWorkspace, language }) => {
  const { workspaceUuid } = useParams();
  const navigate = useNavigate();
  const isRtl = language === 'arabic';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f6f6f7', color: '#1a1c1d', direction: isRtl ? 'rtl' : 'ltr' }}>
      <Navbar onLogout={handleLogout} user={user} workspaceUuid={workspaceUuid} workspaceName={currentWorkspace?.name} />
      <style>{`
        .main-content { flex:1; padding:24px; transition: all .2s ease; }
        @media (min-width: 720px) {
          body[dir='rtl'] .main-content { margin-right: 215px; margin-left: 0 !important; }
          body:not([dir='rtl']) .main-content { margin-left: 215px; margin-right: 0 !important; }
        }
        @media (max-width: 719px) { .main-content { margin-left:0 !important; margin-right:0 !important; padding-top: 64px; } }
      `}</style>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [preToken, setPreToken] = useState(null);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [language, setLanguage] = useState('english');
  const t = translations[language] || translations.english;
  const isRtl = language === 'arabic';

  useEffect(() => {
    if (language === 'arabic') {
      document.body.dir = 'rtl';
      document.documentElement.lang = 'ar';
      document.body.style.fontFamily = "'Geeza Pro', 'Segoe UI', Tahoma, sans-serif";
    } else {
      document.body.dir = 'ltr';
      document.documentElement.lang = 'en';
      document.body.style.fontFamily = "'Inter', system-ui, -apple-system, sans-serif";
    }
  }, [language]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          if (localStorage.getItem('token')) {
            handleLogout();
            setSessionExpired(true);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedWorkspace = localStorage.getItem('currentWorkspace');

      if (!token) {
        setIsAuthenticated(false);
        setIsAuthChecked(true);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        const res = await axios.get(`${API_BASE}/users/me`);
        setUser(res.data);
        setIsAuthenticated(true);
        if (savedWorkspace) {
          const ws = JSON.parse(savedWorkspace);
          setCurrentWorkspace(ws);
          if (ws.language) setLanguage(ws.language);
        }
      } catch (err) {
        handleLogout();
      } finally {
        setIsAuthChecked(true);
      }
    };
    checkAuth();
  }, []);

  const [loginError, setLoginError] = useState('');

  const handleLogin = async (email, password) => {
    setLoginError('');
    setSessionExpired(false);
    try {
      const res = await axios.post(`${API_BASE}/users/login`, { email, password });

      if (res.data.singleWorkspace) {
        const { token, user: userData, workspaces: wsList } = res.data;
        localStorage.setItem('token', token);
        localStorage.setItem('currentWorkspace', JSON.stringify(wsList[0]));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        setCurrentWorkspace(wsList[0]);
        if (wsList[0].language) setLanguage(wsList[0].language);
        setIsAuthenticated(true);
      } else {
        // Multi-workspace flow
        setPreToken(res.data.preToken);
        setWorkspaces(res.data.workspaces);
        setUser(res.data.user);
      }
    } catch (err) {
      setLoginError(translations[language].login_error_creds);
    }
  };

  const handleSelectWorkspace = async (workspaceId) => {
    setLoginError('');
    try {
      const res = await axios.post(`${API_BASE}/users/select-workspace`, {
        preToken,
        workspaceId
      });
      const { token, workspace, user: userData } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('currentWorkspace', JSON.stringify(workspace));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(userData);
      setCurrentWorkspace(workspace);
      if (workspace.language) setLanguage(workspace.language);
      setPreToken(null);
      setWorkspaces([]);
      setIsAuthenticated(true);
    } catch (err) {
      setLoginError(err.response?.data?.msg || translations[language].failed_select_workspace);
    }
  };

  const handleLogout = () => {
    setLoginError('');
    localStorage.removeItem('token');
    localStorage.removeItem('currentWorkspace');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setCurrentWorkspace(null);
    setLanguage('english');
    setPreToken(null);
    setWorkspaces([]);
    setIsAuthenticated(false);
  };

  if (!isAuthChecked) return <Loading type="full" message={translations[language].secure_signin} />;

  return (
    <LanguageProvider language={language} setLanguage={setLanguage}>
      <Router>
        {sessionExpired && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', direction: isRtl ? 'rtl' : 'ltr' }}>
          <div style={{ background: '#fff', padding: 40, borderRadius: 12, textAlign: 'center' }}>
            <h2>{t.session_expired_title}</h2>
            <p>{t.session_expired_msg}</p>
            <button onClick={() => setSessionExpired(false)} className="btn-primary" style={{ minWidth: 100 }}>{t.ok_btn}</button>
          </div>
        </div>}

        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            !isAuthenticated ? (
              preToken ? (
                <WorkspacePicker workspaces={workspaces} onSelect={handleSelectWorkspace} onLogout={handleLogout} />
              ) : (
                <Login onLogin={handleLogin} error={loginError} />
              )
            ) : (
              <Navigate to={`/workspace/${currentWorkspace?.workspaceUuid}/all-inventory`} replace />
            )
          } />

          <Route path="/add-workspaces" element={<AddWorkspaces />} />

          <Route path="/health" element={<Health />} />

          {/* Workspace Routes */}
          <Route path="/workspace/:workspaceUuid" element={
            isAuthenticated ? (
              <WorkspaceLayout user={user} handleLogout={handleLogout} currentWorkspace={currentWorkspace} language={language} />
            ) : <Navigate to="/login" replace />
          }>
            <Route index element={<Navigate to="all-inventory" replace />} />
            <Route path="all-inventory" element={<AllInventory />} />
            <Route path="raw-materials" element={<RawMaterials />} />
            <Route path="raw-materials/:uuid" element={<RawMaterialDetail />} />
            <Route path="products" element={<Products />} />
            <Route path="products/:uuid" element={<ProductDetail />} />
            <Route path="management" element={<Management user={user} />} />
            <Route path="dispatch" element={<Dispatch />} />
            <Route path="analysis" element={<Analysis />} />
            <Route path="activity" element={<Activity />} />
            <Route path="packing-materials" element={<PackingMaterials />} />
            <Route path="account" element={<Account user={user} language={language} setLanguage={setLanguage} />} />
          </Route>

          <Route path="/" element={
            isAuthenticated
              ? <Navigate to={`/workspace/${currentWorkspace?.workspaceUuid}/all-inventory`} replace />
              : <Navigate to="/login" replace />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </LanguageProvider>
  );
};

export default App;